import { Badge, Icon, type TableColumn, Typography } from "@kyan/ui";

import { getOptionOrPerpConstraints } from "#/config/orders";
import { tooltips } from "#/config/tooltips";
import { LimitOrder, type OpenOrder } from "#/types/orders";
import { copy } from "#/utils/copy";
import { formatAddress } from "#/utils/formatAddress";
import { getInstrumentProperties } from "#/utils/instrument.utils";
import { parseMaturity } from "#/utils/maturity";
import { formatCurrency, formatPercent } from "#/utils/valueFormatters";

import { SizeDisplay } from "../SizeDisplay/SizeDisplay";
import { CancelOrder } from "./components/CancelOrder/CancelOrder";

export const commonColumns: TableColumn<OpenOrder>[] = [
  {
    id: "size",
    header: "Size",
    cell: ({ size, isOption, instrument }) => {
      const { underlying } = getInstrumentProperties(instrument);
      const { sizeDisplayPrecision } = getOptionOrPerpConstraints(instrument);

      return (
        <SizeDisplay
          value={size}
          precision={sizeDisplayPrecision}
          underlying={isOption ? undefined : underlying}
        />
      );
    },
  },
  {
    id: "direction",
    header: "Direction",
    cell: ({ direction }) => (
      <Badge variant={direction} className="capitalize">
        {direction}
      </Badge>
    ),
  },
  {
    id: "limitPrice",
    header: "Limit Price",
    tooltip: tooltips.limitPrice,
    cell: ({ limitPrice }) => formatCurrency(limitPrice),
  },
  {
    id: "filled",
    header: "Filled",
    tooltip: tooltips.filled,
    cell: ({ filled, size, isOption, instrument }) => {
      const { sizeDisplayPrecision } = getOptionOrPerpConstraints(instrument);
      const { underlying } = getInstrumentProperties(instrument);

      return (
        <div className="flex gap-2">
          <SizeDisplay
            compact
            value={filled}
            precision={sizeDisplayPrecision}
            valueClassName="text-textSecondary"
            underlying={isOption ? undefined : underlying}
          />
          <Typography primary>
            {formatPercent(filled / size, { precision: 0 })}
          </Typography>
        </div>
      );
    },
  },
  {
    id: "orderId",
    header: "Order ID",
    minWidth: "150px",
    cellClassName: "text-textPrimary",
    cell: ({ orderId }) => (
      <div className="flex w-full items-center justify-between gap-2">
        <span>{formatAddress(orderId, 4, 10)}</span>
        <Icon
          name="copy"
          className="text-textSecondary hover:text-textPrimary cursor-pointer transition-colors"
          onClick={() => {
            copy(orderId);
          }}
        />
      </div>
    ),
    tooltip: tooltips.orderId,
  },
  {
    id: "tif",
    header: "TIF",
    tooltip: tooltips.tif,
    cell: ({ tif }) => (tif === LimitOrder.GTC ? "GTC" : "GTD"),
  },
  {
    id: "data",
    header: "Date created",
    tooltip: tooltips.data,
    minWidth: "75px",
    cell: (row) => <>{parseMaturity(row.date)}</>,
  },
  {
    id: "cancel",
    header: "Cancel",
    minWidth: "75px",
    cell: ({
      size,
      orderId,
      direction,
      instrument,
      limitPrice,
      isLiquidated,
    }) => (
      <CancelOrder
        size={size}
        orderId={orderId}
        direction={direction}
        instrument={instrument}
        limitPrice={limitPrice}
        isLiquidated={isLiquidated}
      />
    ),
  },
];
